import React, { useCallback, useContext, useMemo, useState } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { useConfig } from '../../../defaultConfig';
import { useAccounts } from '../../../fetchHooks/useAccount';
import { useListExternalAvtalegiros, useMutateMoveAvtalegiros } from '../../../fetchHooks/useAvtalegiro';
import useEFakturaAccountNumber from '../../../fetchHooks/useEFakturaAccount';
import useFavoriteAccount from '../../../fetchHooks/useFavoriteAccount';
import {
    Account,
    AccountNumber,
    ExternalAvtalegiro,
    type MoveExternalAvtalegirosResult,
} from '../../../generated-types';
import { isOwnAccount, isValidAvtalegiroFromAccount } from '../../../services/accountFilters';
import { useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import { ErrorTypes } from '../../../utils/gtm/errorTypes';
import { pushErrorToDataLayer } from '../../../utils/gtm/gtmService';
import externalAvtalegiroMapper from '../externalAvtalegiroMapper';

type MoveAvtaleGiroError = {
    debitAccount: string | undefined;
    selectedAgreements: string | undefined;
};

const INITIAL_ERROR: MoveAvtaleGiroError = {
    debitAccount: undefined,
    selectedAgreements: undefined,
};

type AvtaleGiroMoveState = {
    debitAccount: [AccountNumber | undefined, (accountNumber: AccountNumber) => void];
    selectedDebitAccount: Account | undefined;
    selectedAgreements: [string[], (agreementIdToToggle: string, isInErrorState?: boolean) => void];
    toggleSelectedAgreements: [boolean, (avtaleGirosToToggle: ExternalAvtalegiro[], isInErrorState?: boolean) => void];
    error: [MoveAvtaleGiroError, (keyToSet: keyof MoveAvtaleGiroError, value: string | undefined) => void];
    moveAvtaleGiros: {
        data: MoveExternalAvtalegirosResult[];
        mutate: () => void;
        isSuccess: boolean;
        isError: boolean;
        isSomeError: boolean;
        isPending: boolean;
        error: RequestExceptionWithResponse | null;
        reset: () => void;
    };
    cancel: () => void;
    avtaleGiros: { data: ExternalAvtalegiro[]; isLoading: boolean; error: RequestExceptionWithResponse | null };
    accounts: { data: Account[]; isLoading: boolean; error: RequestExceptionWithResponse | null };
};

const MoveContext = React.createContext<AvtaleGiroMoveState>({
    debitAccount: [undefined, () => null],
    selectedDebitAccount: undefined,
    selectedAgreements: [[], () => null],
    toggleSelectedAgreements: [false, () => null],
    error: [INITIAL_ERROR, () => null],
    moveAvtaleGiros: {
        mutate: () => null,
        data: [],
        isError: false,
        isPending: false,
        isSuccess: false,
        isSomeError: false,
        error: null,
        reset: () => null,
    },
    cancel: () => null,
    avtaleGiros: { data: [], isLoading: false, error: null },
    accounts: { data: [], isLoading: false, error: null },
});

export const useAvtaleGiroMoveContext = () => {
    const context = useContext(MoveContext)! as AvtaleGiroMoveState;
    if (context === undefined) {
        throw new Error('useAvtaleGiroMoveContext must be used within MoveContext');
    }
    return context;
};

const AvtaleGiroMoveContext: React.FC<React.PropsWithChildren> = ({ children }) => {
    const debitAccountState = useState<AccountNumber | undefined>();
    const [debitAccount, setDebitAccount] = debitAccountState;
    const selectedAgreementsState = useState<string[]>([]);
    const [selectedAgreements, setSelectedAgreements] = selectedAgreementsState;
    const toggleSelectedAgreementsState = useState(false);
    const [toggleSelectedAgreements, setToggleSelectedAgreements] = toggleSelectedAgreementsState;
    const [error, setError] = useState<MoveAvtaleGiroError>(INITIAL_ERROR);

    const {
        featureToggles: { enableEFakturaAccount },
    } = useConfig();

    const [isSomeError, setIsSomeError] = useState(false);
    const [moveAvtaleGiroResponse, setMoveAvtaleGiroResponse] = useState<MoveExternalAvtalegirosResult[]>([]);

    const { dynamicLinkId, dispatch, signingCompleted } = useSigningContext();

    const {
        data: avtaleGiros = [],
        error: avtaleGirosError,
        isLoading: avtaleGirosLoading,
    } = useListExternalAvtalegiros(dynamicLinkId?.value, signingCompleted);

    const { data: eFakturaAccount, isLoading } = useEFakturaAccountNumber({
        onSuccess: (data) => setDebitAccount(data),
        enabled: enableEFakturaAccount,
    });
    useFavoriteAccount({
        enabled: (!isLoading && !eFakturaAccount) || !enableEFakturaAccount,
        onSuccess: (data) => setDebitAccount(data),
    });

    const moveAvtalegiroSelector = (accounts: Account[]) =>
        accounts.filter((account) => isValidAvtalegiroFromAccount(account) && isOwnAccount(account));
    const { data: accounts, ...accountState } = useAccounts({ select: moveAvtalegiroSelector });

    const mutateAvtalegiros = useMutateMoveAvtalegiros(dynamicLinkId?.value);

    const moveAvtaleGiros = useCallback(() => {
        if (!debitAccount) return;
        mutateAvtalegiros
            .mutateAsync({
                debitorAccount: debitAccount,
                agreements: avtaleGiros
                    .filter((avtalegiro) => selectedAgreements.includes(avtalegiro.id.value))
                    .map(externalAvtalegiroMapper),
                deviceId: window.ReactNativeProps?.deviceId,
            })
            .then((data) => {
                setMoveAvtaleGiroResponse(data);
                setIsSomeError(data.some((moveAvtaleGiroResponse) => !moveAvtaleGiroResponse.success));
                setSelectedAgreements((prevState) =>
                    prevState.filter((avtaleGiro) =>
                        data.some(
                            (movedAvtaleGiro) => movedAvtaleGiro.id.value === avtaleGiro && !movedAvtaleGiro.success,
                        ),
                    ),
                );
            })
            .catch((error: RequestExceptionWithResponse) => {
                setIsSomeError(false);
                setMoveAvtaleGiroResponse([]);
                pushErrorToDataLayer(error, ErrorTypes.MOVE_AVTALEGIRO);
            });
    }, [avtaleGiros, debitAccount, selectedAgreements, mutateAvtalegiros, setSelectedAgreements]);

    const setErrorByKey = useCallback(
        (keyToSet: keyof MoveAvtaleGiroError, value: string | undefined) => {
            setError((prevState) => ({ ...prevState, [keyToSet]: value }));
        },
        [setError],
    );

    const toggleSelectAllAgreements = useCallback(
        (avtaleGirosToToggle: ExternalAvtalegiro[], isInErrorState = false) => {
            if (!isInErrorState) {
                setIsSomeError(false);
                setMoveAvtaleGiroResponse([]);
                mutateAvtalegiros.reset();
            }
            setErrorByKey('selectedAgreements', undefined);
            setSelectedAgreements(avtaleGirosToToggle.map((avtaleGiro) => avtaleGiro.id.value));
            setToggleSelectedAgreements(avtaleGirosToToggle.length > 0);
        },
        [setErrorByKey, setSelectedAgreements, setToggleSelectedAgreements, mutateAvtalegiros],
    );

    const toggleSelectedAgreement = useCallback(
        (agreementId: string, isInErrorState = false) => {
            if (!isInErrorState) {
                setIsSomeError(false);
                setMoveAvtaleGiroResponse([]);
                mutateAvtalegiros.reset();
            }
            setErrorByKey('selectedAgreements', undefined);
            const agreementsToSet = selectedAgreements.includes(agreementId)
                ? selectedAgreements.filter((a) => a !== agreementId)
                : [...selectedAgreements, agreementId];
            setSelectedAgreements(agreementsToSet);
            setToggleSelectedAgreements(agreementsToSet.length > 0);
        },
        [selectedAgreements, setSelectedAgreements, setErrorByKey, setToggleSelectedAgreements, mutateAvtalegiros],
    );

    const setTargetDebitAccount = useCallback(
        (accountNumber: AccountNumber | undefined) => {
            setErrorByKey('debitAccount', undefined);
            setDebitAccount(accountNumber);
        },
        [setDebitAccount, setErrorByKey],
    );

    const clearError = useCallback(() => setError(INITIAL_ERROR), [setError]);

    const cancelMove = useCallback(() => {
        clearError();
        mutateAvtalegiros.reset();
        dispatch({ type: Actions.RESET });
    }, [clearError, mutateAvtalegiros, dispatch]);

    const contextState = useMemo(
        (): AvtaleGiroMoveState => ({
            debitAccount: [debitAccount, setTargetDebitAccount],
            selectedDebitAccount: accounts?.find((account) => account.accountNumber.value === debitAccount?.value),
            selectedAgreements: [selectedAgreements, toggleSelectedAgreement],
            error: [error, setErrorByKey],
            toggleSelectedAgreements: [toggleSelectedAgreements, toggleSelectAllAgreements],
            moveAvtaleGiros: {
                mutate: moveAvtaleGiros,
                data: moveAvtaleGiroResponse,
                isSomeError,
                isSuccess: mutateAvtalegiros.isSuccess,
                error: mutateAvtalegiros.error,
                isError: mutateAvtalegiros.isError,
                isPending: mutateAvtalegiros.isPending,
                reset: mutateAvtalegiros.reset,
            },
            cancel: cancelMove,
            avtaleGiros: {
                data: avtaleGiros,
                error: avtaleGirosError,
                isLoading: avtaleGirosLoading,
            },
            accounts: {
                data: accounts ?? [],
                isLoading: accountState.isLoading,
                error: accountState.error,
            },
        }),
        [
            setErrorByKey,
            debitAccount,
            setTargetDebitAccount,
            selectedAgreements,
            toggleSelectedAgreement,
            error,
            toggleSelectedAgreements,
            toggleSelectAllAgreements,
            moveAvtaleGiros,
            mutateAvtalegiros,
            moveAvtaleGiroResponse,
            cancelMove,
            avtaleGiros,
            avtaleGirosError,
            avtaleGirosLoading,
            accounts,
            accountState.isLoading,
            accountState.error,
            isSomeError,
        ],
    );

    return <MoveContext.Provider value={contextState}>{children}</MoveContext.Provider>;
};

export default AvtaleGiroMoveContext;
